/**
 * Where the two tokens live — and they live in deliberately different places.
 *
 * **Access token: memory only.** It is the bearer on every call and it lasts 15 minutes. Keeping
 * it out of any storage means a stolen storage dump is not a live session, and the 15-minute TTL
 * caps what a compromised tab is worth.
 *
 * **Refresh token: `sessionStorage`, never `localStorage`.** This is arch-08 §5, restated in
 * `docs/05-admin/02` §2.2, and it is a trade the console makes on purpose: closing the browser
 * costs a re-login, and in exchange the token does not sit on disk between sessions where every
 * other tab, extension and backup can reach it. A staff tool can afford the re-login.
 *
 * The consequence to keep in mind when reading the boot path: **after a reload there is no access
 * token.** There is a refresh token and nothing else, which is why the session bootstrap starts by
 * spending it rather than by calling `/v1/admin/me`.
 */
import type { TokenPair, TokenStore } from '../api/authed';

const REFRESH_KEY = 'bookmypandiit-admin-refresh';

/* Module-scoped, not exported: nothing outside this file should be able to read the bearer. */
let accessToken: string | null = null;

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    /* Private mode, or a browser told to block site data. Signed out is the safe reading. */
    return null;
  }
}

export const tokenStore: TokenStore = {
  read(): TokenPair | null {
    const refresh = storage()?.getItem(REFRESH_KEY) ?? null;
    if (!refresh) return null;
    /* An empty access token is a real state, not a broken one: it is what every page load looks
       like before the refresh token has been spent. The caller refreshes rather than despairs. */
    return { access: accessToken ?? '', refresh };
  },

  write(pair: TokenPair): void {
    accessToken = pair.access;
    storage()?.setItem(REFRESH_KEY, pair.refresh);
  },

  clear(): void {
    accessToken = null;
    storage()?.removeItem(REFRESH_KEY);
  },
};

/** True when this browser session has a refresh token worth spending. */
export function hasStoredSession(): boolean {
  return Boolean(storage()?.getItem(REFRESH_KEY));
}

/** True when a call can be made right now without refreshing first. */
export function hasLiveAccessToken(): boolean {
  return accessToken !== null;
}
